

/**
 * Geocoding service using Nominatim (OpenStreetMap)
 * Free geocoding API with no API key required
 */

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org';
const USER_AGENT = 'GridBalance-Partner-Finder/1.0';

// Rate limiting: Nominatim allows max 1 request per second
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 1000; // 1 second

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const geocodingService = {
  /**
   * Geocode an address to get latitude and longitude
   * @param {Object} address - Address object with street, postalCode, city
   * @returns {Object} - { latitude, longitude, displayName }
   */
  async geocodeAddress(address) {
    try {
      const { street, postalCode, city } = address;
      
      if (!postalCode || !city) {
        throw new Error('Postleitzahl und Stadt sind erforderlich für Geocoding');
      }

      // Build search query
      const searchParts = [];
      if (street) searchParts?.push(street);
      if (postalCode) searchParts?.push(postalCode);
      if (city) searchParts?.push(city);
      searchParts?.push('Deutschland'); // Always search in Germany
      
      const searchQuery = searchParts?.join(', ');

      // Rate limiting: ensure at least 1 second between requests
      const now = Date.now();
      const timeSinceLastRequest = now - lastRequestTime;
      if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
        await delay(MIN_REQUEST_INTERVAL - timeSinceLastRequest);
      }
      lastRequestTime = Date.now();

      // Make request to Nominatim
      const params = new URLSearchParams({
        q: searchQuery,
        format: 'json',
        addressdetails: '1',
        limit: '1',
        countrycodes: 'de' // Limit to Germany
      });

      const response = await fetch(`${NOMINATIM_BASE_URL}/search?${params}`, {
        headers: {
          'User-Agent': USER_AGENT,
          'Accept': 'application/json'
        }
      });

      if (!response?.ok) {
        throw new Error(`Geocoding API Fehler: ${response.status}`);
      }

      const data = await response?.json();

      if (!data || data?.length === 0) {
        throw new Error('Keine Koordinaten für diese Adresse gefunden');
      }

      const result = data?.[0];
      
      return {
        latitude: parseFloat(result?.lat),
        longitude: parseFloat(result?.lon),
        displayName: result?.display_name
      };
    } catch (error) {
      console.error('Geocoding error:', error);
      throw new Error(error?.message || 'Fehler beim Geocoding der Adresse');
    }
  },

  /**
   * Reverse geocode coordinates to get address
   * @param {number} latitude
   * @param {number} longitude
   * @returns {Object} - Address object
   */
  async reverseGeocode(latitude, longitude) {
    try {
      if (!latitude || !longitude) {
        throw new Error('Latitude und Longitude sind erforderlich');
      }

      // Rate limiting
      const now = Date.now();
      const timeSinceLastRequest = now - lastRequestTime;
      if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
        await delay(MIN_REQUEST_INTERVAL - timeSinceLastRequest);
      }
      lastRequestTime = Date.now();

      const params = new URLSearchParams({
        lat: latitude.toString(),
        lon: longitude.toString(),
        format: 'json',
        addressdetails: '1'
      });

      const response = await fetch(`${NOMINATIM_BASE_URL}/reverse?${params}`, {
        headers: {
          'User-Agent': USER_AGENT,
          'Accept': 'application/json'
        }
      });

      if (!response?.ok) {
        throw new Error(`Reverse Geocoding API Fehler: ${response.status}`);
      }

      const data = await response?.json();

      if (!data || !data?.address) {
        throw new Error('Keine Adresse für diese Koordinaten gefunden');
      }

      const addr = data?.address;
      
      return {
        street: addr?.road || '',
        postalCode: addr?.postcode || '',
        city: addr?.city || addr?.town || addr?.village || '',
        displayName: data?.display_name
      };
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      throw new Error(error?.message || 'Fehler beim Reverse Geocoding');
    }
  },

  /**
   * Calculate distance between two coordinates using Haversine formula
   * @param {number} lat1 - Latitude of point 1
   * @param {number} lon1 - Longitude of point 1
   * @param {number} lat2 - Latitude of point 2
   * @param {number} lon2 - Longitude of point 2
   * @returns {number} - Distance in kilometers
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    return distance;
  },

  /**
   * Convert degrees to radians
   */
  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  },

  /**
   * Geocode a postal code to get its center coordinates
   * @param {string} postalCode - German postal code
   * @returns {Object} - { latitude, longitude }
   */
  async geocodePostalCode(postalCode) {
    try {
      if (!postalCode) {
        throw new Error('Postleitzahl ist erforderlich');
      }

      // Rate limiting
      const now = Date.now();
      const timeSinceLastRequest = now - lastRequestTime;
      if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
        await delay(MIN_REQUEST_INTERVAL - timeSinceLastRequest);
      }
      lastRequestTime = Date.now();

      const params = new URLSearchParams({
        postalcode: postalCode,
        country: 'Germany',
        format: 'json',
        limit: '1'
      });

      const response = await fetch(`${NOMINATIM_BASE_URL}/search?${params}`, {
        headers: {
          'User-Agent': USER_AGENT,
          'Accept': 'application/json'
        }
      });

      if (!response?.ok) {
        throw new Error(`Geocoding API Fehler: ${response.status}`);
      }

      const data = await response?.json();

      if (!data || data?.length === 0) {
        throw new Error('Keine Koordinaten für diese Postleitzahl gefunden');
      }

      const result = data?.[0];
      
      return {
        latitude: parseFloat(result?.lat),
        longitude: parseFloat(result?.lon)
      };
    } catch (error) {
      console.error('Postal code geocoding error:', error);
      throw new Error(error?.message || 'Fehler beim Geocoding der Postleitzahl');
    }
  }
};
